import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { useDemo } from '@/contexts/DemoContext';
import { DEMO_PROFILE, DEMO_USER_ID } from '@/lib/demoData';
import { setSentryUser } from '@/lib/sentry';
import type { Json } from '@/integrations/supabase/types';

/**
 * Baut den Audit-Log-Eintrag fuer einen Auth-State-Change. Exportiert als
 * pure helper, damit die DSGVO-Art-30-relevante Payload per Unit-Test
 * verifiziert werden kann – der Provider selbst ist wegen seiner vielen
 * Abhaengigkeiten (supabase, i18n, sentry, demo-ctx) schwer isoliert
 * testbar.
 *
 * Gibt `null` zurueck, wenn fuer das Event kein Audit-Eintrag geschrieben
 * werden soll (z. B. TOKEN_REFRESHED – das ist eine reine Session-
 * Verlaengerung, keine Anmeldung).
 */
export function buildAuthAuditEntry(
  event: string,
  user: Pick<User, 'id' | 'user_metadata'> | null,
  profile: { organization_id: string } | null,
): { action: 'login' | 'logout'; user_id: string; organization_id: string | null; details: Record<string, unknown> } | null {
  if (event === 'SIGNED_IN' && user) {
    return {
      action: 'login',
      user_id: user.id,
      organization_id:
        (user.user_metadata as Record<string, unknown> | undefined)?.organization_id as string | undefined ?? null,
      details: { method: 'email', event },
    };
  }
  if (event === 'SIGNED_OUT' && user) {
    return {
      action: 'logout',
      user_id: user.id,
      organization_id: profile?.organization_id ?? null,
      details: { event },
    };
  }
  return null;
}

export type Profile = {
  id: string;
  user_id: string;
  organization_id: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'therapeut' | 'assistenz' | 'org_admin';
  assistant_name: string | null;
  /**
   * Per-user dashboard layout, persisted as JSON. Optional because
   * older accounts will not have it set yet — Dashboard.tsx falls back
   * to {@link DEFAULT_LAYOUT} when this is null/undefined.
   */
  dashboard_layout?: Json | null;
  /**
   * Persönliche Reihenfolge der primären Topbar-Tabs (Bookmark-Leisten-
   * Muster), persistiert als `string[]` von Route-IDs. Siehe
   * `src/features/navTabOrder.ts`. `null`/unset = Code-Standardreihenfolge.
   */
  nav_tab_order?: Json | null;
  /** Optional super-admin flag — only set on a handful of accounts. */
  is_superadmin?: boolean | null;
  /** Opt-in to anonymized data use for non-profit research. */
  research_consent?: boolean | null;
};

type AuthContextType = {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /** Meldet den Nutzer auf allen Geräten gleichzeitig ab (scope: global). */
  signOutAll: () => Promise<void>;
  /**
   * Re-read the current user's profile from Supabase and push the
   * fresh row into the context. No-op in demo mode and when there is
   * no logged-in user. Callers that mutate a profile column (e.g.
   * the Dashboard widget editor) use this to keep the context in
   * sync with the database after a successful write.
   */
  refreshProfile: () => Promise<void>;
  /**
   * Optimistically patch the in-memory profile without hitting the
   * database. Used for the same reason as `refreshProfile` but for
   * cases where the caller already knows the new value and does not
   * want to pay for a round-trip (or cannot hit the DB at all, as is
   * the case for demo sessions).
   */
  updateLocalProfile: (patch: Partial<Profile>) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_KEY_STORAGE = 'nf_device_session_key';

function parseUserAgent(ua: string): { browser: string; os: string; device_name: string } {
  let browser = 'Unbekannt';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  let os = 'Unbekannt';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  const device_name = `${browser} auf ${os}`;
  return { browser, os, device_name };
}

async function registerDeviceSession(userId: string): Promise<void> {
  let sessionKey = sessionStorage.getItem(SESSION_KEY_STORAGE);
  if (!sessionKey) {
    sessionKey = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY_STORAGE, sessionKey);
  }
  const { browser, os, device_name } = parseUserAgent(navigator.userAgent);
  // upsert by session_key: insert on first login, update last_seen_at on refresh
  await supabase.from('device_sessions').upsert(
    { user_id: userId, session_key: sessionKey, browser, os, device_name, last_seen_at: new Date().toISOString() },
    { onConflict: 'session_key' },
  );
}

async function removeDeviceSession(): Promise<void> {
  const sessionKey = sessionStorage.getItem(SESSION_KEY_STORAGE);
  if (!sessionKey) return;
  await supabase.from('device_sessions').delete().eq('session_key', sessionKey);
  sessionStorage.removeItem(SESSION_KEY_STORAGE);
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const { isDemo, demoRole, exitDemo } = useDemo();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Refs für stabile Referenzen im onAuthStateChange-Callback
  const userRef = useRef<User | null>(null);
  const profileRef = useRef<Profile | null>(null);

  const loadProfile = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    const p = data as Profile | null;
    setProfile(p);
    profileRef.current = p;
    // Tag every Sentry event with the org id so support can filter by
    // tenant. We deliberately pass *only* the org id — never the user
    // email or name — to keep the breadcrumb DSGVO-clean.
    setSentryUser(p?.organization_id ?? null);
  }, []);

  const refreshProfile = useCallback(async () => {
    // No-op in demo mode (see demo branch below for the override) and
    // when there is no session to read from.
    if (isDemo) return;
    const uid = userRef.current?.id;
    if (!uid) return;
    await loadProfile(uid);
  }, [isDemo, loadProfile]);

  const updateLocalProfile = useCallback((patch: Partial<Profile>) => {
    setProfile(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      profileRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      userRef.current = session?.user ?? null;
      if (session?.user) {
        await loadProfile(session.user.id);
        await registerDeviceSession(session.user.id);
        const entry = buildAuthAuditEntry(event, session.user, null);
        if (entry) {
          await supabase.from('audit_logs').insert({
            ...entry,
            details: entry.details as Json,
          });
        }
      } else {
        // Refs nutzen, da State in der Closure veraltet sein kann.
        const entry = buildAuthAuditEntry(event, userRef.current, profileRef.current);
        if (entry) {
          await supabase.from('audit_logs').insert({
            ...entry,
            details: entry.details as Json,
          });
        }
        setProfile(null);
        profileRef.current = null;
        // Clear the Sentry user tag so the next session does not inherit
        // it. Same reason we set it on load: clean tenant filtering.
        setSentryUser(null);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      userRef.current = session?.user ?? null;
      if (session?.user) {
        await loadProfile(session.user.id);
      }
      setLoading(false);
    });

    // ── Stale-session watchdog ────────────────────────────────────────
    //
    // Users have hit a scenario where the refresh token has been
    // invalidated server-side (see browser console: `AuthApiError:
    // Invalid Refresh Token: Refresh Token Not Found`) but the client
    // keeps the old access token in memory. The dashboard keeps
    // rendering, every DB query returns 403 / 400, and the UI gets
    // stuck in a half-authenticated limbo until the tab is closed.
    //
    // Defensively verify the session when the tab regains focus. If
    // Supabase no longer considers it valid, clear our local state so
    // `ProtectedRoute` redirects the user to `/login` where they can
    // re-authenticate.
    const verifySession = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!userRef.current) return;
      const { data } = await supabase.auth.getSession();
      if (data.session) return;
      // Session is gone server-side — surface a friendly toast and let
      // ProtectedRoute redirect on the next render.
      toast.info('Sitzung abgelaufen – bitte neu anmelden.');
      setSession(null);
      setUser(null);
      setProfile(null);
      userRef.current = null;
      profileRef.current = null;
      setSentryUser(null);
    };
    document.addEventListener('visibilitychange', verifySession);
    window.addEventListener('focus', verifySession);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', verifySession);
      window.removeEventListener('focus', verifySession);
    };
    // `loadProfile` is memoised with an empty dep array so its reference
    // is stable for the lifetime of the provider. Including it here
    // satisfies exhaustive-deps without changing runtime behaviour.
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    if (isDemo) {
      exitDemo();
      return;
    }
    await removeDeviceSession();
    await supabase.auth.signOut();
    setProfile(null);
  }, [isDemo, exitDemo]);

  const signOutAll = useCallback(async () => {
    if (isDemo) {
      exitDemo();
      return;
    }
    // Lösche alle device_sessions des Nutzers, dann globaler Supabase-Logout.
    if (userRef.current) {
      await supabase.from('device_sessions').delete().eq('user_id', userRef.current.id);
      sessionStorage.removeItem(SESSION_KEY_STORAGE);
    }
    await supabase.auth.signOut({ scope: 'global' });
    setProfile(null);
  }, [isDemo, exitDemo]);

  // BSI-konformer Inaktivitäts-Timeout: 15 Minuten, Warnung bei 13 Minuten.
  // Schützt Patientendaten wenn der Therapeut den Rechner unbeaufsichtigt lässt.
  useInactivityTimeout({
    enabled: !!session && !isDemo,
    onWarning: () => {
      toast.warning(t('auth.inactivityWarningTitle'), {
        description: t('auth.inactivityWarningBody'),
        duration: 120_000,
        id: 'inactivity-warning',
      });
    },
    onTimeout: async () => {
      toast.dismiss('inactivity-warning');
      await signOut();
      toast.info(t('auth.inactivityTimeoutTitle'), {
        description: t('auth.inactivityTimeoutBody'),
      });
    },
  });

  // Demo-Modus: Therapeuten-Rolle
  if (isDemo && demoRole === 'therapeut') {
    return (
      <DemoAuthProvider
        signOut={signOut}
      >
        {children}
      </DemoAuthProvider>
    );
  }

  return (
    <AuthContext.Provider
      value={{ user, session, profile, loading, signOut, signOutAll, refreshProfile, updateLocalProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Separate provider for demo sessions. Keeping it in its own component
 * lets us use a local `useState` for the demo profile so the dashboard
 * widget editor (and anything else that calls `updateLocalProfile`) can
 * mutate it without touching Supabase — the demo user's UUID is not a
 * real UUID, so any DB write would be rejected with `22P02 invalid
 * input syntax for type uuid`.
 */
const DemoAuthProvider = ({
  children,
  signOut,
}: {
  children: React.ReactNode;
  signOut: () => Promise<void>;
}) => {
  const [profile, setProfile] = useState<Profile>(DEMO_PROFILE as Profile);
  const demoSession = { user: { id: DEMO_USER_ID, email: 'demo@nutriflow-pro.de' } } as unknown as Session;
  const demoUser = { id: DEMO_USER_ID, email: 'demo@nutriflow-pro.de' } as unknown as User;

  const updateLocalProfile = useCallback((patch: Partial<Profile>) => {
    setProfile(prev => ({ ...prev, ...patch }));
  }, []);
  const refreshProfile = useCallback(async () => {}, []);

  return (
    <AuthContext.Provider
      value={{
        user: demoUser,
        session: demoSession,
        profile,
        loading: false,
        signOut,
        signOutAll: signOut,
        refreshProfile,
        updateLocalProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
